import { useCallback, useRef } from "react"
import ZebraBrowserPrintWrapper from "zebra-browser-print-wrapper"

import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UseZplPrintingReturn {
  printZpl: (zpl: string) => Promise<boolean>
}

export const useZplPrinting = (): UseZplPrintingReturn => {
  const printingNotificationIdRef = useRef<number | null>(null)

  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const dismissNotification = useNotificationsStore((state) => state.dismissNotification)

  const initBrowserPrint = useCallback(async (): Promise<ZebraBrowserPrintWrapper | null> => {
    try {
      const browserPrint = new ZebraBrowserPrintWrapper()

      const availablePrinters = await browserPrint.getAvailablePrinters()

      const printer = availablePrinters[0]

      if (!printer) {
        throw new Error("No printer(s) found")
      }

      browserPrint.setPrinter(printer)

      const status = await browserPrint.checkPrinterStatus()
      if (!status.isReadyToPrint) {
        throw new Error("Printer is not ready")
      }

      return browserPrint
    } catch (error) {
      console.error("Printer initialization error:", error)
      notify({
        type: NotificationType.Error,
        message: "Printer initialization error",
      })
      return null
    }
  }, [notify])

  const printZpl = useCallback(
    async (zpl: string): Promise<boolean> => {
      try {
        const browserPrint = await initBrowserPrint()

        if (!browserPrint) {
          return false
        }

        const startNotificationId = Date.now()
        printingNotificationIdRef.current = startNotificationId
        notify({
          id: startNotificationId,
          type: NotificationType.Info,
          silent: false,
          message: "Printing QR code...",
          autoHideDuration: 10000,
        })

        await browserPrint.print(zpl)

        dismissNotification(startNotificationId)

        notify({
          type: NotificationType.Info,
          silent: false,
          message: "Your QR codes are processing and printing. Please wait before clicking the Print button again.",
        })

        return true
      } catch (error) {
        if (printingNotificationIdRef.current) {
          dismissNotification(printingNotificationIdRef.current)
        }

        notify({
          type: NotificationType.Error,
          message: `Print error: ${error instanceof Error ? error.message : "Failed to print"}`,
        })
        return false
      } finally {
        printingNotificationIdRef.current = null
      }
    },
    [initBrowserPrint, notify, dismissNotification]
  )

  return { printZpl }
}
